import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type SubscriberJSON = z.infer<typeof SubscriberSchema.SubscriberJSONZ>;
export type SubscriberParams = z.infer<typeof SubscriberSchema.SubscriberZ>;

export default class SubscriberSchema {
  public static readonly SubscriberJSONZ = z.object({
    userId: z.uuid(),
    legalEntityId: z.uuid().or(z.literal(Guid.EMPTY.value))
  });

  public static readonly SubscriberZ = z.object({
    userId: z.instanceof(Guid),
    legalEntityId: z.instanceof(Guid),
  });

  public static validate(values: unknown): SubscriberParams {
    return parseOrThrow(SubscriberSchema.SubscriberZ, values, {
      logError: true,
      schemaName: "SubscriberSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): SubscriberJSON {
    return parseOrThrow(SubscriberSchema.SubscriberJSONZ, json, {
      logError: true,
      schemaName: "SubscriberSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is SubscriberParams {
    try {
      SubscriberSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is SubscriberJSON {
    try {
      SubscriberSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: SubscriberJSON): SubscriberParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(SubscriberSchema.SubscriberJSONZ, json, {
      logError: true,
      schemaName: "SubscriberSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return SubscriberSchema.codec.decode(parsed);
  }

  public static serialize(params: SubscriberParams): SubscriberJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = SubscriberSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return SubscriberSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(SubscriberSchema.SubscriberJSONZ, SubscriberSchema.SubscriberZ, {
    decode: (data) => ({
      userId: Guid.fromJSON(data.userId)!,
      legalEntityId: Guid.fromJSON(data.legalEntityId, { allowEmpty: true })!,
    }),
    encode: (subscriber) => ({
      userId: subscriber.userId.toJSON(),
      legalEntityId: subscriber.legalEntityId.toJSON(),
    }),
  });
}
